import { useState } from "react";
import { toast } from "sonner";
import { useRefundTrayDeposit } from "../../hooks/useOrders";
import type { Order, TrayDepositEligibility } from "../../services/ordersApi";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, FieldLabel } from "@/components/ui/field";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface RefundTrayDepositDialogProps {
  order: Order;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eligibility: TrayDepositEligibility;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-KE", {
    style: "currency",
    currency: "KES",
    minimumFractionDigits: 2,
  }).format(value);

export const RefundTrayDepositDialog = ({
  order,
  open,
  onOpenChange,
  eligibility,
}: RefundTrayDepositDialogProps) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Refund Tray Deposit</DialogTitle>
          <DialogDescription>
            Refund requires storekeeper-approved tray returns covering the
            requested quantity.
          </DialogDescription>
        </DialogHeader>
        {/* Keyed by `open` so the form state resets on every open */}
        {open && (
          <RefundTrayDepositForm order={order} eligibility={eligibility} onClose={() => onOpenChange(false)} />
        )}
      </DialogContent>
    </Dialog>
  );
};

interface RefundTrayDepositFormProps {
  order: Order;
  onClose: () => void;
  eligibility: TrayDepositEligibility;
}

const RefundTrayDepositForm = ({ order, eligibility, onClose }: RefundTrayDepositFormProps) => {
  const [trayQuantity, setTrayQuantity] = useState("");
  const [trayType, setTrayType] = useState<210 | 442>(
    eligibility.availableQuantity210Hole > 0 ? 210 : 442
  );
  const [refundMethod, setRefundMethod] = useState<"Wallet" | "FinancePayout">("Wallet");
  const [notes, setNotes] = useState("");

  const refundMutation = useRefundTrayDeposit();

  const depositAmount = Number(order.trayDepositAmount ?? 0);
  const refunded = Number(order.trayDepositRefunded ?? 0);
  const refundable = depositAmount - refunded;
  const rate = trayType === 210 ? eligibility.rate210Hole : eligibility.rate442Hole;
  const availableForType = trayType === 210
    ? eligibility.availableQuantity210Hole
    : eligibility.availableQuantity442Hole;
  const maxTrays = Math.min(availableForType, eligibility.eligibleReturnedQuantity);

  const handleSubmit = () => {
    const qty = parseFloat(trayQuantity);
    if (!Number.isInteger(qty) || qty <= 0 || qty > maxTrays) {
      toast.error(`Enter a whole tray quantity between 1 and ${maxTrays}`);
      return;
    }

    refundMutation.mutate(
      {
        orderId: order.id,
        payload: { trayQuantity: qty, trayType, refundMethod, notes: notes || undefined },
      },
      {
        onSuccess: () => {
          onClose();
          toast.success(
            refundMethod === "Wallet"
              ? "Tray deposit refund credited to the customer wallet (pending approval)"
              : "Tray deposit payout flagged for finance to process"
          );
        },
      }
    );
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-sm">
        <div>
          <p className="text-muted-foreground text-xs">Deposit</p>
          <p className="font-medium">{formatCurrency(depositAmount)}</p>
        </div>
        <div>
          <p className="text-muted-foreground text-xs">Refunded / reserved</p>
          <p className="font-medium">{formatCurrency(refunded)}</p>
        </div>
        <div>
          <p className="text-muted-foreground text-xs">Refundable</p>
          <p className="font-medium text-emerald-700">{formatCurrency(refundable)}</p>
        </div>
      </div>

      <Field>
        <FieldLabel>Tray Type *</FieldLabel>
        <Select
          value={String(trayType)}
          onValueChange={(value) => setTrayType(Number(value) as 210 | 442)}
          disabled={refundMutation.isPending}
        >
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {eligibility.availableQuantity210Hole > 0 && <SelectItem value="210">210-hole tray</SelectItem>}
            {eligibility.availableQuantity442Hole > 0 && <SelectItem value="442">442-hole tray</SelectItem>}
          </SelectContent>
        </Select>
      </Field>

      <Field>
        <FieldLabel>Tray Quantity *</FieldLabel>
        <Input
          type="number"
          min="1"
          max={maxTrays}
          step="1"
          value={trayQuantity}
          onChange={(e) => setTrayQuantity(e.target.value)}
          placeholder={`Up to ${maxTrays}`}
          disabled={refundMutation.isPending}
        />
        {rate > 0 && (
          <p className="text-xs text-muted-foreground mt-1">
            {formatCurrency(rate)} per tray · up to {maxTrays} approved, unclaimed tray(s)
          </p>
        )}
      </Field>

      <Field>
        <FieldLabel>Refund Destination *</FieldLabel>
        <Select
          value={refundMethod}
          onValueChange={(v) => setRefundMethod(v as "Wallet" | "FinancePayout")}
          disabled={refundMutation.isPending}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Wallet">Customer wallet credit</SelectItem>
            <SelectItem value="FinancePayout">Finance external payout</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <Field>
        <FieldLabel>Notes</FieldLabel>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional notes for audit"
          disabled={refundMutation.isPending}
        />
      </Field>

      <div className="flex gap-2 justify-end pt-4">
        <Button variant="secondary" onClick={onClose} disabled={refundMutation.isPending}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={refundMutation.isPending || maxTrays <= 0}>
          {refundMutation.isPending ? "Processing..." : "Refund Deposit"}
        </Button>
      </div>
    </div>
  );
};
